import { gql } from '@apollo/client';

/**
 * People the caller may share WITH (scio3#2351) — the share picker's own question, replacing its use
 * of GET_COMPANY_USERS (the account roster, which is Admin+).
 *
 * Two modes and no third. `ids` point-resolves already-granted users, because stored security keeps
 * raw user ids and the dialog would otherwise render them verbatim. `search` returns candidates and
 * needs at least 2 characters. Empty arguments return nothing: you find a colleague you already
 * know, rather than listing the company.
 */
export const SHAREABLE_MEMBERS = gql`
  query shareableMembers($ids: [String!], $search: String, $teamId: String, $first: Int) {
    shareableMembers(ids: $ids, search: $search, teamId: $teamId, first: $first) {
      id
      name
      email
      username
    }
  }
`;

/**
 * Counts for the Group Security page's two SYNTHETIC rows (scio3#2351). That page used to derive
 * them by reading the whole account roster and calling `.length`; the roster is Admin+ now, so a
 * `groups.view` holder would have seen both rows read 0. Counts are readable by any account member —
 * HOW MANY is already visible wherever a group or teamspace is listed, WHO is not.
 */
export const ACCOUNT_MEMBER_COUNTS = gql`
  query accountMemberCounts {
    accountMemberCounts {
      total
      superAdmins
    }
  }
`;

/**
 * Query to get all users in the company/account
 * Returns users with their id, name, email, and role
 *
 * `sources` (#2282) is HOW each person reaches the requested teamspace: `direct` = a roster row,
 * `group` = an expansion of a group's role ref, which has NO roster row behind it. `removeMember`
 * writes only the roster, so a row whose sources are all `group` cannot be removed here at all —
 * `MemberTable` reads this to withhold Remove and name the group instead of silently no-op'ing.
 * Same shape as `WORKSPACE_MEMBERS_QUERY`'s `sources`; one vocabulary for one fact.
 */
export const GET_COMPANY_USERS = gql`
  query getCompanyUsers($teamId: String) {
    getCompanyUsers(teamId: $teamId) {
      id
      name
      email
      username
      role
      date
      canEditHomePath
      multiFactorConfig {
        totp
        phone
      }
      sources {
        type
        groupId
        groupName
        role
      }
    }
  }
`;

/**
 * ALL human users in the account (users collection by accountId) — INCLUDES users who belong only to a
 * sub-team, which `getCompanyUsers` (company.users) misses. Admin-only; used by the Group Security
 * member pickers. Backend: user.resolver `accountUsers`.
 */
export const ACCOUNT_USERS_QUERY = gql`
  query accountUsers {
    accountUsers {
      id
      name
      email
      username
    }
  }
`;

/**
 * Account-wide USER DIRECTORY for the Users page — every human in the account, each row carrying the
 * teamspaces it belongs to (`teams`, EMPTY when the user sits in none, which the page shows as
 * "No team"). OWNER / SUPER_ADMIN only; every other role receives [] and the page falls back to the
 * per-teamspace roster (`GET_COMPANY_USERS`), whose behaviour is unchanged.
 *
 * Distinct from `ACCOUNT_USERS_QUERY` above: that one is the lightweight id/name/email picker feed;
 * this one carries the full roster row (role, added date, 2FA) the table renders.
 * Backend: user.resolver `accountUserDirectory`.
 */
export const ACCOUNT_USER_DIRECTORY_QUERY = gql`
  query accountUserDirectory {
    accountUserDirectory {
      id
      name
      email
      username
      role
      date
      canEditHomePath
      multiFactorConfig {
        totp
        phone
      }
      teams {
        _id
        name
      }
    }
  }
`;

/**
 * Each directory user's role IN each of their teamspaces (direct, or conferred by a group grant).
 * Split from `ACCOUNT_USER_DIRECTORY_QUERY` on purpose: `UserTeamRef.role` is newer than the
 * directory, and a backend without it rejects the WHOLE query — which used to drop the Users page
 * back to the Default-teamspace roster. Asked separately, an older backend only loses the badges.
 * Fetch with `no-cache`: rows share `TeamUser:<id>` with the directory, and a cached write here
 * would replace their `teams` (dropping names).
 */
export const ACCOUNT_USER_DIRECTORY_TEAM_ROLES_QUERY = gql`
  query accountUserDirectoryTeamRoles {
    accountUserDirectory {
      id
      teams {
        _id
        role
      }
    }
  }
`;

/**
 * Query to get all teams in the account
 * Returns teams with their _id, name, description, and createdAt
 */
/**
 * Mutation to log scio3→scio1 switch and update user.scioVersion=1
 */
export const SCIO_SWITCH_LOGS = gql`
  mutation scioSwitchLogs($teamId: String) {
    scioSwitchLogs(teamId: $teamId)
  }
`;

export const SAVE_TEAM = gql`
  mutation SaveTeam($payload: TeamInput!) {
    saveTeam(payload: $payload) {
      _id
      name
    }
  }
`;

export const GET_ALL_TEAMS = gql`
  query getAllTeams($teamId: String, $first: Int, $sort: TeamSort, $ids: [String!]) {
    getAllTeams(teamId: $teamId, first: $first, sort: $sort, ids: $ids) {
      page {
        edges {
          node {
            _id
            name
            description
            createdAt
            isTeamBilling
            purpose
            # §12 HOME PATH at the teamspace level — Edit Teamspace seeds its toggle from this, so
            # omitting it would silently reset the flag to false on every save from that dialog.
            canEditHomePath
            limitation {
              maxQuantity
              quantity
            }
          }
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
        }
      }
      pageData {
        limit
        offset
      }
    }
  }
`;
